import {
  Box,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CustomTextField from "../../common/CustomTextField";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  Cancel,
  SaveOutlined,
  DoNotDisturbOnOutlined,
  HelpOutline,
  VerifiedUserOutlined,
  DescriptionOutlined,
  PersonOutline,
} from "@mui/icons-material";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { useLocation } from "react-router-dom";
import { SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { convertBase64 } from "../../../utils/Utility";
import { BENEFIT_SUMMARY_STATUS } from "../../../constants/applicationConstants";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { UpdateIVRObj, fetchIVRById, updateElementInCreateIVRSlice, updateIVRRespond, useCreateIVRSlice } from "../../../redux/slice/ivr/createIVRSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface ExtendedIVRObj extends UpdateIVRObj {
  action: string
}

const BenefitSummary = () => {
  const {
    control,
    handleSubmit,
    getValues,
    setError,
    setValue,
    clearErrors,
    register,
    formState: { errors },
  } = useForm<ExtendedIVRObj>();

  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { IVRObject } = useCreateIVRSlice();
  const { IVRListUpdateStatus } = useCreateIVRSlice();

  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: '', show: false });

  const {
    tokens: mTokens,
    isDark: isDarkModal,
    inputFieldSx,
    cancelBtnSx,
  } = useModalStyles();

  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };

  const orderDetails = location?.state?.orderDetails;

  const insuranceInfo = useMemo(()=>{
    const obj = IVRObject?.patient?.patientInfo?.find((data)=>
      data.infoType === "PRIMARY_INSURANCE"
    )
    return obj
  },[IVRObject])

  useEffect(() => {
    setValue("ivrId", orderDetails?.ivrId);
    setValue("note.noteType", "BENEFIT_SUMMARY");
    if (orderDetails?.ivrId) {}
      dispatch(fetchIVRById({ivrId:(orderDetails.ivrId).toString(), isSaveAuditLog: false}))
  }, []);

  const handleClickDeny = (noteContent: string) => {
    if(!noteContent || noteContent?.length === 0) {
      setValue('note.attachment', '')
    };
    setValue("verificationStatus", BENEFIT_SUMMARY_STATUS.DENIED);
  }; 
  
  const handleClickNeedMoreInfo = (noteContent: string) => {
    if(!noteContent || noteContent?.length === 0) {
      setValue('note.attachment', '')
    };
    setValue("verificationStatus", BENEFIT_SUMMARY_STATUS.NEED_MORE_INFO);
  };
  const handleClickPreAuth = (noteContent: string) => {
    if(!noteContent || noteContent?.length === 0) {
      setValue('note.attachment', '')
    };
    setValue("verificationStatus", BENEFIT_SUMMARY_STATUS.PRE_AUTH);
  };

  const handleClickVerify = () => {
    clearErrors('note.content');
    setValue("verificationStatus", BENEFIT_SUMMARY_STATUS.VERIFIED);
  };

  const setBenefitSummaryError = () => {
    setError('note.attachment', {
      type: 'required',
      message: "Benefit Summary (Attachment) is required."
    });
    let errorObj: SingleAlertInfo = {
      message: "Please attach the Benefit Summary.",
      alertType: "error"
    };
    dispatch(setSingleAlertObj(errorObj));
  }
 
  const onSubmit = () => {
    const action = getValues('action');
    const noteContent = getValues('note.attachment');

    if(action === BENEFIT_SUMMARY_STATUS.VERIFIED) {
      if(!noteContent || noteContent?.length === 0) {
        setBenefitSummaryError();
        return;
      };
      handleClickVerify();
    } else if (action === BENEFIT_SUMMARY_STATUS.NEED_MORE_INFO) {
      handleClickNeedMoreInfo(noteContent);
    } else if (action === BENEFIT_SUMMARY_STATUS.PRE_AUTH) {
      handleClickPreAuth(noteContent);
    } else if (action === BENEFIT_SUMMARY_STATUS.DENIED) {
      handleClickDeny(noteContent);
    };
    IVRAction();
  };

  const IVRAction = () => {
    setShowOptionsPopup({message: "Do you really want to update the IVR? ", 
    show: true, 
    variant: "confirm", 
    buttons: [{name: "Confirm", color: 'primary', onClick:() => updateIVR() },{name: "Cancel", color: 'secondary', onClick:cancelOptionPopup }]});
  }

  const updateIVR = async () => {
    const {action, ...updateIVRData} = getValues();
    const response = await dispatch(updateIVRRespond(updateIVRData));
    cancelOptionPopup();
  };

  useEffect(() => {
    if(IVRListUpdateStatus === 'success') {
      let successObj: SingleAlertInfo = {
        message: `IVR ${orderDetails?.ivrNumber} has been updated successfully.`,
        alertType: "success"
      };
      dispatch(setSingleAlertObj(successObj));
      dispatch(updateElementInCreateIVRSlice(
       { elementName : "IVRListUpdateStatus",
        value: "idle"}
      ))
      navigate("/ivr_list", {state: {withSearchHistory: true}});
    }
  }, [IVRListUpdateStatus])

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const handleDropzoneChange = async (uploadedFiles: ExtendedFile[], fieldName: any) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      const base64File = String(await convertBase64(uploadedFiles[0]));
      const fileName = uploadedFiles[0].name;
      const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
      const base64Substring = base64File.substring(
        fileExtension === "pdf" ? "data:application/pdf;base64,".length  : "data:image/png;base64,".length, 
        base64File.length);
      setValue("note.attachment", base64Substring);
      setValue("note.documentName", fileName);
      setValue("note.fileType", fileExtension);

      // Clear the error when file is uploaded
      clearErrors("note.attachment");
    }
  };

  const handleDeleteFile = () => {
    setValue("note.attachment", "");
    setValue("note.documentName", "");
    setValue("note.fileType", "");
  }

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.9, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box display="flex" alignItems="center" py={1.15} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600, flex: 1 }} component="div">
        {value ?? "—"}
      </Typography>
    </Box>
  );

  const outlinedActionBtnSx = (color: string) => ({
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.88rem",
    borderRadius: "10px",
    py: 1.2,
    color,
    borderColor: `${color}66`,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: color,
      backgroundColor: `${color}12`,
      boxShadow: "none",
    },
    "& .MuiTypography-root": { color: `${color} !important` },
    "& .MuiButton-startIcon": { color },
  });

  return (
    <Box
      sx={{
        mx: { xs: "10px", sm: "20px" },
        mt: "12px",
        pb: "72px",
      }}
    >
      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}

      <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
        <PageBackButton tooltip={null} onClick={() => navigate(-1, { state: { withSearchHistory: true } })} />
        <Box data-testid="mock-header">
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            IVR / Benefit Summary
          </Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
            Benefit Summary
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
            Review benefits and record the verification outcome.
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2, alignItems: "flex-start" }}>
          <Box sx={{ ...cardSx, order: { xs: 2, md: 1 } }}>
            <SectionHeader icon={<DescriptionOutlined fontSize="small" />} title="Benefit Summary Details" />
            <Box sx={{ px: 3.2, pt: 2.2, pb: 3.2 }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <FieldLabel label="Attachment" required />
                  <Dropzone
                    name="note.attachment"
                    onFileChange={() => {}}
                    onFileChangeTest={handleDropzoneChange}
                    onDeleteFile={handleDeleteFile}
                    control={control}
                    maxFileCount={1}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel label="Notes" required />
                  <Box sx={inputFieldSx(!!errors.note?.content)}>
                    <CustomTextField
                      controllerName="note.content"
                      control={control}
                      label=""
                      multiline
                      rows={4}
                      rules={{ required: "Notes are required." }}
                      maxRows={4}
                      fullWidth
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" flexWrap="wrap" gap={1.2} justifyContent="flex-end" mt={1}>
                    <Button
                      variant="outlined"
                      type="submit"
                      startIcon={<VerifiedUserOutlined />}
                      removeUpperCase
                      onClick={() => {
                        setValue("action", BENEFIT_SUMMARY_STATUS.PRE_AUTH);
                      }}
                      sx={{ ...outlinedActionBtnSx("#6366F1"), minWidth: { sm: 130 } }}
                    >
                      PRE-AUTH
                    </Button>
                    <Button
                      variant="outlined"
                      type="submit"
                      startIcon={<DoNotDisturbOnOutlined />}
                      removeUpperCase
                      onClick={() => {
                        setValue("action", BENEFIT_SUMMARY_STATUS.DENIED);
                      }}
                      sx={{ ...outlinedActionBtnSx("#F43F5E"), minWidth: { sm: 110 } }}
                    >
                      Deny
                    </Button>
                    <Button
                      variant="outlined"
                      type="submit"
                      startIcon={<HelpOutline />}
                      removeUpperCase
                      onClick={() => {
                        setValue("action", BENEFIT_SUMMARY_STATUS.NEED_MORE_INFO);
                      }}
                      sx={{ ...outlinedActionBtnSx("#F59E0B"), minWidth: { sm: 160 } }}
                    >
                      Need More Info
                    </Button>
                    <Button
                      type="submit"
                      variant="outlined"
                      startIcon={<SaveOutlined />}
                      removeUpperCase
                      onClick={() => {
                        setValue("action", BENEFIT_SUMMARY_STATUS.VERIFIED);
                      }}
                      sx={{ ...outlinedActionBtnSx(mTokens.accent), minWidth: { sm: 130 } }}
                    >
                      Verify
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      removeUpperCase
                      onClick={() =>
                        navigate(-1, { state: { withSearchHistory: true } })
                      }
                      sx={{
                        ...cancelBtnSx,
                        minWidth: { sm: 110 },
                        "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                        "& .MuiButton-startIcon": { color: mTokens.error },
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ ...cardSx, order: { xs: 1, md: 2 } }}>
            <SectionHeader icon={<PersonOutline fontSize="small" />} title="Patient Info" />
            <Box sx={{ px: 3.2, py: 2 }}>
              <InfoRow label="Patient" value={orderDetails ? orderDetails.patient : ""} />
              <InfoRow label="DOB" value={orderDetails ? orderDetails.patientDOB : ""} />
              <InfoRow label="Insurance Info" value={insuranceInfo?.patientinfoData?.insuranceName} />
              <InfoRow label="Policy Number" value={insuranceInfo?.patientinfoData?.policy} />
            </Box>
          </Box>
        </Box>
        <input type="hidden" {...register("action")} />
      </form>
    </Box>
  );
};

export default BenefitSummary;
